import { InboundIcon } from "@minket/brand/icons/inbound";
import { StatusBadge } from "@minket/ui/components/status-badge";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { messageStatus } from "@/components/inbound/model";
import { VerdictLamps } from "@/components/inbound/verdicts";
import { ItemView } from "@/components/page/item";
import { Page } from "@/components/page/page";
import { Sheet } from "@/components/page/section";
import { endpoints } from "@/lib/endpoints";
import { apiQuery } from "@/lib/query";
import { useWorkspace } from "@/lib/workspace";

const MessageSections = lazy(() =>
  import("@/components/inbound/message.ondemand").then((module) => ({
    default: module.MessageSections,
  })),
);

export const Route = createFileRoute("/inbound/$id")({
  component: ReceivedMessage,
});

const sections = [
  { title: "Envelope", description: "Who sent it and who it was for.", fields: ["From", "To"] },
  { title: "Verdicts", description: "SPF, DKIM, DMARC, spam and virus, from SES." },
  { title: "Routing", description: "Where it went." },
];

/** One received message's whole record (direction §7, Inbound): for links and narrow screens. */
function ReceivedMessage() {
  const { id } = Route.useParams();
  const { project } = useWorkspace();
  const params = { projectId: project?.id ?? "", inboundId: id };
  const query = useQuery({ ...apiQuery(endpoints.inboundMessage, params), enabled: !!project });
  const message = query.data;
  const status = message ? messageStatus[message.status] : null;
  const subject = message ? message.subject || "(no subject)" : "Received message";
  return (
    <Page
      title={subject}
      documentTitle={`${subject} · Received`}
      serial={{ id, label: "message id" }}
      back={{ to: "/inbound", label: "Received" }}
      description={
        message && status ? (
          <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
            <VerdictLamps verdicts={message.verdicts} />
          </span>
        ) : undefined
      }
    >
      <ItemView
        icon={InboundIcon}
        noun="this message"
        will="The message's envelope, its verdicts and its routing will show here."
        sections={sections}
        scope="project"
        query={(projectId) => apiQuery(endpoints.inboundMessage, { projectId, inboundId: id })}
        notFound={{ kind: "Message", back: { to: "/inbound", label: "Received" } }}
      >
        {(detail) => (
          <Sheet>
            <Suspense fallback={null}>
              <MessageSections message={detail} page />
            </Suspense>
          </Sheet>
        )}
      </ItemView>
    </Page>
  );
}
